import { ActionTree } from "vuex";
import {
    SET_USER_REQUESTS,
    SET_SINGLE_REQUEST,
    SET_BRANDS,
    SET_COUNTRIES,
    SET_AFFILIATES,
    SET_TYPES_OF_CAMPAIGN,
    SET_TYPES_OF_ASSETS,
    SET_MEDIA,
    SET_REQUEST_STATUS,
    SET_IS_REFERENCE_DATA_LOADED,
    SET_REQUEST_MESSAGES,
    SET_UPLOAD_FILES,
    SET_STATS_REQUEST,
    SET_GLOBAL_SETTINGS,
    SET_DECISION_MESSAGES,
    SET_THEMES,
} from "./mutations-types";
import { IRequestState } from "./state";
import { authService, NotificationService, ResponseService, QueryService } from "@/services";
import { IMessage, IRequest, IRequestFile, IFileKeys, IStatisticsResponse, IGlobalSettings, IMessageReaction } from "@/models";

export const actions: ActionTree<IRequestState, any> = {
    // Requests
    async fetchUserRequests(context, parameters: Array<[string, string]>): Promise<void> {
        const query = QueryService.buildQueryParameters(parameters);
        const response = await fetch(process.env.VUE_APP_AZURE_FUNCTION_BASE_URL + "/request" + query, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
        });
        const userRequests = await response.json();
        context.commit(SET_USER_REQUESTS, userRequests);
    },

    async fetchSingleRequest(context, requestId: number): Promise<void> {
        const response = await fetch(`${process.env.VUE_APP_AZURE_FUNCTION_BASE_URL}/request/${requestId}`, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
        });
        if (response.ok) {
            const request = await response.json();
            context.commit(SET_SINGLE_REQUEST, request);
            context.commit(SET_REQUEST_MESSAGES, request.messages);
        } else {
            NotificationService.showError("Request not found");
            context.commit(SET_SINGLE_REQUEST, null);
            context.commit(SET_REQUEST_MESSAGES, null);
        }
    },

    clearAllSingleRequestData(context): void {
        context.commit(SET_SINGLE_REQUEST, null);
        context.commit(SET_UPLOAD_FILES, null);
        context.commit(SET_REQUEST_MESSAGES, null);
    },

    async createOrUpdateUserRequest(context, data): Promise<number> {
        const response: Response = await fetch(process.env.VUE_APP_AZURE_FUNCTION_BASE_URL + `/request`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
            body: JSON.stringify(data),
        });

        const message: string = await ResponseService.createMessage(response, "Request");
        NotificationService.show(response, message);
        context.dispatch("fetchUserRequests");
        if (response.ok) {
            const request: IRequest = await response.json();
            return request?.requestId;
        } else {
            return null;
        }
    },

    async deleteUserRequest(context, requestId: number): Promise<boolean> {
        const response = await fetch(`${process.env.VUE_APP_AZURE_FUNCTION_BASE_URL}/request/${requestId}`, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
        });

        const message: string = await ResponseService.createMessage(response, "Request");
        NotificationService.show(response, message);
        if (response.ok) {
            context.dispatch("fetchUserRequests");
        }

        return response.ok;
    },

    // Get UploadFiles
    async fetchUploadFiles(context, key: string): Promise<void> {
        const response = await fetch(`${process.env.VUE_APP_AZURE_FUNCTION_BASE_URL}/request/${key}/file/`, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
        });
        const uploadFiles = await response.json();
        context.commit(SET_UPLOAD_FILES, uploadFiles);
    },

    // Delete Files
    async deleteFiles(context, file: IRequestFile): Promise<boolean> {
        // tslint:disable-next-line:max-line-length
        const response: Response = await fetch(process.env.VUE_APP_AZURE_FUNCTION_BASE_URL + `/request/${file.requestKey}/file/`, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
            body: JSON.stringify(file),
        });

        if (response.ok) {
            const messageDelete: string = await ResponseService.createMessage(response, "File");
            NotificationService.show(response, messageDelete);
        } else {
            NotificationService.showError("Can not delete file");
        }

        await context.dispatch("fetchUploadFiles", file.requestKey);
        return response.ok;
    },

    async registerFiles(context, data: IFileKeys): Promise<void> {
        await fetch(process.env.VUE_APP_AZURE_FUNCTION_BASE_URL + `/request/${data.requestKey}/file/`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
            body: JSON.stringify(data),
        });
    },

    // Messages
    async fetchRequestMessages(context, requestId: number): Promise<void> {
        const response = await fetch(`${process.env.VUE_APP_AZURE_FUNCTION_BASE_URL}/request/${requestId}/message`, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
        });
        const messages = await response.json();
        context.commit(SET_REQUEST_MESSAGES, messages);
    },

    async addRequestMessage(context, data: IMessage): Promise<boolean> {
        // Todo: EM: Tester data.
        const response: Response = await fetch(`${process.env.VUE_APP_AZURE_FUNCTION_BASE_URL}/request/${data.requestId}/message`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
            body: JSON.stringify(data),
        });

        const message: string = await ResponseService.createMessage(response, "Message");
        NotificationService.show(response, message);
        await context.dispatch("fetchRequestMessages", data.requestId);
        return response.ok;
    },

    async addRequestMessageReaction(context, data: IMessageReaction): Promise<boolean> {
        const response: Response = await fetch(`${process.env.VUE_APP_AZURE_FUNCTION_BASE_URL}/request/${data.requestId}/message/${data.messageId}/reaction`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
            body: JSON.stringify(data),
        });

        await context.dispatch("fetchRequestMessages", data.requestId);

        return response.ok;
    },



    // Brands - Countries - Affiliates - Media - Request status
    async fetchReferenceData(context): Promise<void> {
        const response = await fetch(process.env.VUE_APP_AZURE_FUNCTION_BASE_URL + "/referencedata/", {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
        });
        const fetchReferenceData = await response.json();
        context.commit(SET_BRANDS, fetchReferenceData.brands);
        context.commit(SET_COUNTRIES, fetchReferenceData.countries);
        context.commit(SET_AFFILIATES, fetchReferenceData.affiliates);
        context.commit(SET_TYPES_OF_CAMPAIGN, fetchReferenceData.typesOfCampaign);
        context.commit(SET_TYPES_OF_ASSETS, fetchReferenceData.typesOfAssets);
        context.commit(SET_MEDIA, fetchReferenceData.media);
        context.commit(SET_REQUEST_STATUS, fetchReferenceData.requestStatuses);
        context.commit(SET_IS_REFERENCE_DATA_LOADED, true);
        context.commit(SET_DECISION_MESSAGES, fetchReferenceData.decisionMessages);
        context.commit(SET_THEMES, fetchReferenceData.themes);
    },

    // Global Settings
    async fetchGlobalSettings(context): Promise<void> {
        const response: Response = await fetch(process.env.VUE_APP_AZURE_FUNCTION_BASE_URL + "/global", {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
        });
        const globalSettings = await response.json();

        context.commit(SET_GLOBAL_SETTINGS, globalSettings);
    },

    async saveGlobalSettings(context, data: IGlobalSettings): Promise<boolean> {
        const response: Response = await fetch(process.env.VUE_APP_AZURE_FUNCTION_BASE_URL + `/global/save/`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
            body: JSON.stringify(data),
        });

        const message: string = await ResponseService.createMessage(response, "Global Settings");
        NotificationService.show(response, message);
        await context.dispatch("fetchGlobalSettings");
        return response.ok;
    },

    // Add - Edit - Delete Brands
    async addBrand(context, data): Promise<boolean> {
        const response: Response = await fetch(process.env.VUE_APP_AZURE_FUNCTION_BASE_URL + "/brand/", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
            body: JSON.stringify(data),
        });

        const message: string = await ResponseService.createMessage(response, "Brand");
        NotificationService.show(response, message);
        await context.dispatch("fetchReferenceData");
        return response.ok;
    },

    async saveEditBrand(context, data): Promise<boolean> {
        const response: Response = await fetch(process.env.VUE_APP_AZURE_FUNCTION_BASE_URL + `/brand/${data.brandId}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
            body: JSON.stringify(data),
        });

        const message: string = await ResponseService.createMessage(response, "Brand");
        NotificationService.show(response, message);
        await context.dispatch("fetchReferenceData");
        return response.ok;
    },

    async deleteBrand(context, data): Promise<boolean> {
        const response: Response = await fetch(process.env.VUE_APP_AZURE_FUNCTION_BASE_URL + `/brand/${data.brandId}`, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
            body: JSON.stringify(data),
        });

        const message: string = await ResponseService.createMessage(response, "Brand");
        NotificationService.show(response, message);
        await context.dispatch("fetchReferenceData");
        return response.ok;
    },

    // Add - Edit - Delete Countries
    async addCountry(context, data): Promise<boolean> {
        const response: Response = await fetch(process.env.VUE_APP_AZURE_FUNCTION_BASE_URL + "/country/", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
            body: JSON.stringify(data),
        });

        const message: string = await ResponseService.createMessage(response, "Country");
        NotificationService.show(response, message);
        await context.dispatch("fetchReferenceData");
        return response.ok;
    },

    async saveEditCountry(context, data): Promise<boolean> {
        const response: Response = await fetch(process.env.VUE_APP_AZURE_FUNCTION_BASE_URL + `/country/${data.countryId}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
            body: JSON.stringify(data),
        });

        const message: string = await ResponseService.createMessage(response, "Country");
        NotificationService.show(response, message);
        await context.dispatch("fetchReferenceData");
        return response.ok;
    },

    async deleteCountry(context, data): Promise<boolean> {
        const response: Response = await fetch(process.env.VUE_APP_AZURE_FUNCTION_BASE_URL + `/country/${data.countryId}`, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
            body: JSON.stringify(data),
        });

        const message: string = await ResponseService.createMessage(response, "Country");
        NotificationService.show(response, message);
        await context.dispatch("fetchReferenceData");
        return response.ok;
    },

    // Add - Edit - Delete Affiliates
    async addAffiliate(context, data): Promise<boolean> {
        const response: Response = await fetch(process.env.VUE_APP_AZURE_FUNCTION_BASE_URL + "/affiliate/", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
            body: JSON.stringify(data),
        });

        const message: string = await ResponseService.createMessage(response, "Affiliate");
        NotificationService.show(response, message);
        await context.dispatch("fetchReferenceData");
        return response.ok;
    },

    async saveEditAffiliate(context, data): Promise<boolean> {
        const response: Response = await fetch(process.env.VUE_APP_AZURE_FUNCTION_BASE_URL + `/affiliate/${data.affiliateId}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
            body: JSON.stringify(data),
        });

        const message: string = await ResponseService.createMessage(response, "Affiliate");
        NotificationService.show(response, message);
        await context.dispatch("fetchReferenceData");
        return response.ok;
    },

    async deleteAffiliate(context, data): Promise<boolean> {
        const response: Response = await fetch(process.env.VUE_APP_AZURE_FUNCTION_BASE_URL + `/affiliate/${data.affiliateId}`, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
            body: JSON.stringify(data),
        });
        const message: string = await ResponseService.createMessage(response, "Affiliate");
        NotificationService.show(response, message);
        await context.dispatch("fetchReferenceData");
        return response.ok;
    },

    // Request Statistics
    async getStatistics(context,  parameters: Array<[string, string]>): Promise<void> {
        const query = QueryService.buildQueryParameters(parameters);
        const response: Response = await fetch(process.env.VUE_APP_AZURE_FUNCTION_BASE_URL + "/statistic" + query, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
        });
        const statsRequests = await response.json();
        context.commit(SET_STATS_REQUEST, statsRequests);
    },

    async exportStatistics(context,  parameters: IStatisticsResponse): Promise<Response> {
        return await fetch(process.env.VUE_APP_AZURE_FUNCTION_BASE_URL + "/statistic/export/", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
            body: JSON.stringify(parameters),
        });
    },

    async exportDetailledStatistics(context,  parameters: IStatisticsResponse): Promise<Response> {
        return await fetch(process.env.VUE_APP_AZURE_FUNCTION_BASE_URL + "/statistic/exportDetailled/", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
            body: JSON.stringify(parameters),
        });
    },
};
